/**
 * Two API clients for the two backends this module reads from:
 *   - sssApiClient: the Staff Audit Scheduler backend (branches, clients,
 *     engagements, bookings, position rates, and auth -- /auth/login and
 *     /auth/microsoft both live here).
 *   - trsApiClient: the Timesheet (TRS) backend (time entries).
 *
 * Both share the SAME JWT (confirmed: SSS and TRS accept the same token,
 * same user table/secret) and the same X-Branch-ID/401-handling logic --
 * factored into createApiClient() below instead of duplicated.
 */
import axios from 'axios';

/** Shared EventTarget other parts of the app (AuthContext) already listen on. */
export const authEvents = new EventTarget();

const TOKEN_STORAGE_KEY = 'auth_token';
const ACTIVE_BRANCH_STORAGE_KEY = 'active_branch_id';

/**
 * Reads the active branch id set by BranchContext. Kept as a plain
 * localStorage read (not a React hook) so it can be used from this
 * non-component module.
 * @returns {string|null}
 */
function getActiveBranchId() {
  try {
    return localStorage.getItem(ACTIVE_BRANCH_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @param {string} baseURL
 * @returns {import('axios').AxiosInstance}
 */
function createApiClient(baseURL) {
  const client = axios.create({
    baseURL,
    headers: { Accept: 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const branchId = getActiveBranchId();
    if (branchId) {
      config.headers['X-Branch-ID'] = branchId;
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        authEvents.dispatchEvent(new CustomEvent('unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return client;
}

/** Staff Audit Scheduler backend -- branches, clients, engagements, bookings, position rates, auth. */
export const sssApiClient = createApiClient(import.meta.env.VITE_API_BASE_URL ?? '/api/v1');

/** Timesheet (TRS) backend -- time entries. */
export const trsApiClient = createApiClient(import.meta.env.VITE_TRS_API_BASE_URL ?? '/trs-api/v1');

export const WIP_STORAGE_KEYS = {
  TOKEN: TOKEN_STORAGE_KEY,
  ACTIVE_BRANCH: ACTIVE_BRANCH_STORAGE_KEY,
};
